/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client'

import { useAtomValue } from 'jotai'
import Script from 'next/script'
import { useEffect } from 'react'

import { analyticalConsentAtom } from '@/store/cookies'

/**
 * Wrapper for the Google Tag Manager component that sets the default consent as denied for everything. The component
 * is subscribed to the {@link analyticalConsentAtom} to track changes to the user's consent.
 */
const GoogleTagManager = () => {
  const analyticalConsent = useAtomValue(analyticalConsentAtom)
  const tagManagerId = process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID

  /**
   * Update the analytics consent in GTM when the user changes their preferences.
   */
  useEffect(() => {
    if (analyticalConsent !== undefined) {
      updateAnalyticsConsent(analyticalConsent)
    }
  }, [analyticalConsent])

  if (!tagManagerId) return null

  return (
    <>
      <Script
        id='gtm-default-consent'
        strategy='afterInteractive'
        dangerouslySetInnerHTML={{
          __html: `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('consent', 'default', {
              'ad_storage': 'denied',
              'ad_user_data': 'denied',
              'ad_personalization': 'denied',
              'analytics_storage': 'denied'
            });
          `,
        }}
      />
      <Script
        id={'gtm-script'}
        strategy='afterInteractive'
        src={`https://www.googletagmanager.com/gtm.js?id=${tagManagerId}`}
      />
      <Script
        id='gtm-initialise'
        strategy='afterInteractive'
        dangerouslySetInnerHTML={{
          __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${tagManagerId}');
          `,
        }}
      />
    </>
  )
}

/**
 * Updates the analytics consent in GTM.
 *
 * @param consent - the consent value, true for granted, false for denied
 */
const updateAnalyticsConsent = (consent: boolean): void => {
  if (typeof window !== 'undefined' && window.gtag) {
    const consentConfig: Gtag.ConsentParams = {
      analytics_storage: consent ? 'granted' : 'denied',
    }

    window.gtag('consent', 'update', consentConfig)
  }
}

export { GoogleTagManager, updateAnalyticsConsent }
